import { CheckCircle2 } from 'lucide-react'
import type { FormEvent, ReactNode } from 'react'

import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyInput } from '@/components/money-input'
import type { ApiError } from '@/lib/problem-details'
import type { Pesewas } from '@/lib/money'
import { cn } from '@/lib/utils'

/**
 * The form every round operation is filled in on, and the three fields they are built from.
 *
 * Like the group command dialogs, it has two faces: before the click it is a form, and after a
 * countersigned command it stops being a form and shows what actually happened. An operator who
 * watches a form disappear reads that as "done" whatever is written elsewhere, so the result
 * replaces the inputs rather than sitting under them.
 */
export function OperationForm({
  title,
  description,
  notice,
  outcome,
  submitLabel,
  tone = 'default',
  isPending = false,
  disabled = false,
  error = null,
  onSubmit,
  onDone,
  children,
  footnote,
}: {
  title: string
  description?: string
  /** The dual-control notice, where the action is countersigned. */
  notice?: ReactNode
  /** Present once the command has answered; the form is replaced by it. */
  outcome?: ReactNode
  submitLabel: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  disabled?: boolean
  error?: ApiError | null
  onSubmit: () => void
  /** Where "Close" goes once the command has answered. */
  onDone?: () => void
  children: ReactNode
  footnote?: ReactNode
}) {
  const settled = outcome !== undefined && outcome !== null

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!disabled && !isPending) {
      onSubmit()
    }
  }

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
      </CardHeader>

      {settled ? (
        <>
          <CardContent className="space-y-3">{outcome}</CardContent>
          <CardFooter>
            <Button variant="primary" onClick={onDone}>
              Back to the round
            </Button>
          </CardFooter>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <CardContent className="space-y-4">
            {notice}
            {children}
            {error ? <ProblemAlert error={error} /> : null}
            {footnote ? <p className="text-2xs text-muted">{footnote}</p> : null}
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              variant={tone === 'danger' ? 'danger' : 'primary'}
              disabled={disabled || isPending}
            >
              {isPending ? 'Working…' : submitLabel}
            </Button>
          </CardFooter>
        </form>
      )}
    </Card>
  )
}

/** A labelled block, so every field on these forms wires its help text the same way. */
export function FormField({
  id,
  label,
  hint,
  required = false,
  className,
  children,
}: {
  id: string
  label: string
  hint?: ReactNode
  required?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-danger-text" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      {children}
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/**
 * A GHS amount.
 *
 * Always this component, never a text input: the value handed back is an integer count of
 * pesewas parsed with integer arithmetic, so a keyed 1,234.56 reaches the ledger as 123456 and
 * cannot arrive a pesewa light.
 */
export function AmountField({
  id,
  label,
  hint,
  value,
  onChange,
  currency,
  autoFocus = false,
  required = true,
}: {
  id: string
  label: string
  hint?: ReactNode
  value: Pesewas | null
  onChange: (value: Pesewas | null) => void
  currency?: string
  autoFocus?: boolean
  required?: boolean
}) {
  return (
    <FormField id={id} label={label} hint={hint} required={required}>
      <MoneyInput
        id={id}
        value={value}
        onChange={onChange}
        currency={currency}
        autoFocus={autoFocus}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
    </FormField>
  )
}

/**
 * A date.
 *
 * Seeded from the branch's business date wherever one is being posted into, and never from the
 * browser's clock — see `business-day.tsx`.
 */
export function DateField({
  id,
  label,
  hint,
  value,
  onChange,
  required = true,
  disabled = false,
}: {
  id: string
  label: string
  hint?: ReactNode
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
}) {
  return (
    <FormField id={id} label={label} hint={hint} required={required}>
      <Input
        id={id}
        type="date"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
    </FormField>
  )
}

/** A reason, a narration, a reference. Trimmed by the caller before it is sent. */
export function NoteField({
  id,
  label,
  hint,
  value,
  onChange,
  placeholder,
  required = false,
  autoFocus = false,
  multiline = false,
}: {
  id: string
  label: string
  hint?: ReactNode
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  autoFocus?: boolean
  multiline?: boolean
}) {
  return (
    <FormField id={id} label={label} hint={hint} required={required}>
      {multiline ? (
        <textarea
          id={id}
          rows={3}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
        />
      ) : (
        <Input
          id={id}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={hint ? `${id}-hint` : undefined}
        />
      )}
    </FormField>
  )
}

/** A whole number — an instalment count, a number of days. */
export function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
  min,
  max,
  required = true,
}: {
  id: string
  label: string
  hint?: ReactNode
  value: string
  onChange: (value: string) => void
  min?: number
  max?: number
  required?: boolean
}) {
  return (
    <FormField id={id} label={label} hint={hint} required={required}>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        step={1}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
    </FormField>
  )
}

/**
 * What happened, for the operations that are not countersigned.
 *
 * These say "done" because they are done: the command applied, the round on the screen behind is
 * already being re-read, and there is no queue for it to be sitting in.
 */
export function AppliedOutcome({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>{children}</AlertDescription>
      </div>
    </Alert>
  )
}
